import React, { useState } from 'react';
import {
  Trash,
  File,
  Calendar,
  ListChecks,
  Robot,
  MagnifyingGlass,
  CaretDown,
  CaretRight,
  X,
  TreeStructure,
  Database,
  GitBranch,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SimpleDropdown } from '@/components/ui/simple-dropdown';
import { Badge } from '@/components/ui/badge';
import {
  AnswerOptionCondition,
  AnswerOptionConditionType,
  AILocationHints,
  ConditionGroup,
  ConditionNode,
  isConditionGroup,
} from 'shared/types/due-diligence';
import { FileExistsEditor } from './ConditionBuilder/editors/FileExistsEditor';
import { ExpirationDateEditor } from './ConditionBuilder/editors/ExpirationDateEditor';

interface LinkedLabel {
  id: number;
  name: string;
}

/** Extraction question that can be referenced for comparison */
interface ExtractionQuestion {
  fieldName: string;
  displayName: string;
  extractionLabel?: string | null;
  extractionValueType?: 'number' | 'string' | 'date' | 'boolean' | null;
}

/** Question with answer options for QUESTION_ANSWER condition type */
interface AvailableQuestion {
  id: number;
  displayName: string;
  fieldName: string;
  answerOptions?: {
    id?: number; // Optional for unsaved options
    label: string;
    value: string;
    statusColor: string;
    active: boolean;
  }[];
}

interface AnswerOptionConditionBuilderProps {
  /** Root condition group - the top level is always a group */
  conditionRoot: ConditionGroup | null;
  onChange: (root: ConditionGroup | null) => void;
  /** Linked document labels - when multiple, user can specify which label a condition applies to */
  linkedLabels?: LinkedLabel[];
  /** Available extraction questions from previous questions (for DATABASE_COMPARISON) */
  extractionQuestions?: ExtractionQuestion[];
  /** All questions in the category (for QUESTION_ANSWER condition type) */
  availableQuestions?: AvailableQuestion[];
  /** Current question ID to exclude from QUESTION_ANSWER dropdown */
  currentQuestionId?: number;
}

// Condition type metadata
const CONDITION_TYPES = [
  {
    type: AnswerOptionConditionType.FILE_EXISTS,
    label: 'Bestand aanwezig',
    icon: File,
    description: 'Controleert of een bestand met het gekoppelde label bestaat',
  },
  {
    type: AnswerOptionConditionType.EXPIRATION_DATE,
    label: 'Vervaldatum',
    icon: Calendar,
    description: 'Controleert de verloopdatum van het document',
  },
  {
    type: AnswerOptionConditionType.FIELD_VALUE,
    label: 'Veld controle',
    icon: ListChecks,
    description: 'Controleert een specifiek veld op het document',
  },
  {
    type: AnswerOptionConditionType.AI_QUESTION,
    label: 'AI Vraag',
    icon: Robot,
    description: 'Stel een ja/nee vraag aan AI over het document',
  },
  {
    type: AnswerOptionConditionType.AI_REFERENCE,
    label: 'AI Referentie zoeken',
    icon: MagnifyingGlass,
    description: 'Laat AI een specifieke waarde zoeken in het document',
  },
  {
    type: AnswerOptionConditionType.DATABASE_COMPARISON,
    label: 'Database vergelijking',
    icon: Database,
    description: 'Vergelijk document waarde met database veld (bijv. aantal eenheden)',
  },
  {
    type: AnswerOptionConditionType.QUESTION_ANSWER,
    label: 'Antwoord andere vraag',
    icon: GitBranch,
    description: 'Controleert het antwoord van een andere vraag in deze categorie',
  },
];

// FileLog field options
const FILELOG_FIELDS = [
  { value: 'status', label: 'Status' },
  { value: 'signStatus', label: 'Ondertekenstatus' },
  { value: 'expirationDate', label: 'Vervaldatum' },
  { value: 'documentDate', label: 'Document datum' },
];

// Enum values for each FileLog field
const FIELD_ENUM_VALUES: Record<string, { value: string; label: string }[]> = {
  status: [
    { value: 'Ok', label: 'Ok' },
    { value: 'Ok, maar met opmerkingen', label: 'Ok, maar met opmerkingen' },
    { value: 'Niet nodig', label: 'Niet nodig' },
    { value: 'Niet ok', label: 'Niet ok' },
    { value: 'Onbekend of na te kijken', label: 'Onbekend of na te kijken' },
  ],
  signStatus: [
    { value: 'Getekend', label: 'Getekend' },
    { value: 'Ontwerp', label: 'Ontwerp' },
    { value: 'Niet van toepassing', label: 'Niet van toepassing' },
    { value: 'Onbekend / Na te kijken', label: 'Onbekend / Na te kijken' },
  ],
};

// Generate unique ID
const generateId = () => `cond_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Create a new empty group
const createNewGroup = (logic: 'AND' | 'OR' = 'AND'): ConditionGroup => ({
  id: generateId(),
  type: 'group',
  logic,
  children: [],
});

// Create a new condition
const createNewCondition = (type: AnswerOptionConditionType): AnswerOptionCondition => ({
  id: generateId(),
  type,
  ...(type === AnswerOptionConditionType.FILE_EXISTS && {
    fileExists: true,
  }),
  ...(type === AnswerOptionConditionType.EXPIRATION_DATE && {
    expirationComparison: 'more_than_days' as const,
    expirationDays: 30,
  }),
  ...(type === AnswerOptionConditionType.FIELD_VALUE && {
    field: 'status',
    operator: '==' as const,
    value: FIELD_ENUM_VALUES['status'][0].value, // 'Ok'
  }),
  ...(type === AnswerOptionConditionType.AI_QUESTION && {
    aiQuestion: '',
    expectedAnswer: 'yes' as const,
  }),
  ...(type === AnswerOptionConditionType.AI_REFERENCE && {
    aiReference: '',
  }),
  ...(type === AnswerOptionConditionType.DATABASE_COMPARISON && {
    databaseField: '',
    documentValueSource: 'aiExtraction' as const,
    dbComparisonOperator: '==' as const,
    dbComparisonExpected: 'match' as const,
  }),
  ...(type === AnswerOptionConditionType.QUESTION_ANSWER && {
    referencedQuestionId: undefined,
    expectedOptionId: undefined,
    questionAnswerComparison: 'equals' as const,
  }),
});

export const AnswerOptionConditionBuilder: React.FC<AnswerOptionConditionBuilderProps> = ({
  conditionRoot,
  onChange,
  linkedLabels = [],
  extractionQuestions = [],
  availableQuestions = [],
  currentQuestionId,
}) => {
  const [isExpanded, setIsExpanded] = useState(conditionRoot !== null && conditionRoot.children.length > 0);
  const hasMultipleLabels = linkedLabels.length > 1;
  const hasLinkedLabels = linkedLabels.length > 0;
  const hasOtherQuestions = availableQuestions.length > 0;

  // Filter available condition types based on context
  const availableConditionTypes = CONDITION_TYPES.filter((condType) => {
    // Document-related conditions require linked labels
    if ([
      AnswerOptionConditionType.FILE_EXISTS,
      AnswerOptionConditionType.EXPIRATION_DATE,
      AnswerOptionConditionType.FIELD_VALUE,
      AnswerOptionConditionType.AI_QUESTION,
      AnswerOptionConditionType.AI_REFERENCE,
      AnswerOptionConditionType.DATABASE_COMPARISON,
    ].includes(condType.type)) {
      return hasLinkedLabels;
    }
    // QUESTION_ANSWER requires other questions to exist
    if (condType.type === AnswerOptionConditionType.QUESTION_ANSWER) {
      return hasOtherQuestions;
    }
    return true;
  });

  // Count total conditions (including nested)
  const countConditions = (node: ConditionNode | null): number => {
    if (!node) return 0;
    if (isConditionGroup(node)) {
      return node.children.reduce((sum, child) => sum + countConditions(child), 0);
    }
    return 1;
  };

  const totalConditions = countConditions(conditionRoot);

  // Initialize root if needed
  const ensureRoot = (): ConditionGroup => {
    if (conditionRoot) return conditionRoot;
    return createNewGroup('AND');
  };

  // Handle adding a condition to a group
  const handleAddCondition = (groupId: string, type: AnswerOptionConditionType) => {
    const root = ensureRoot();
    const newCondition = createNewCondition(type);

    const addToGroup = (node: ConditionNode): ConditionNode => {
      if (isConditionGroup(node)) {
        if (node.id === groupId) {
          return { ...node, children: [...node.children, newCondition] };
        }
        return { ...node, children: node.children.map(addToGroup) };
      }
      return node;
    };

    const updatedRoot = addToGroup(root) as ConditionGroup;
    onChange(updatedRoot);
    setIsExpanded(true);
  };

  // Handle adding a nested group
  const handleAddGroup = (parentGroupId: string) => {
    const root = ensureRoot();
    const newGroup = createNewGroup('AND');

    const addGroupToParent = (node: ConditionNode): ConditionNode => {
      if (isConditionGroup(node)) {
        if (node.id === parentGroupId) {
          return { ...node, children: [...node.children, newGroup] };
        }
        return { ...node, children: node.children.map(addGroupToParent) };
      }
      return node;
    };

    const updatedRoot = addGroupToParent(root) as ConditionGroup;
    onChange(updatedRoot);
  };

  // Handle removing a node (condition or group)
  const handleRemoveNode = (nodeId: string) => {
    if (!conditionRoot) return;

    const removeFromGroup = (node: ConditionNode): ConditionNode | null => {
      if (isConditionGroup(node)) {
        if (node.id === nodeId) return null;
        const updatedChildren = node.children
          .map(removeFromGroup)
          .filter((child): child is ConditionNode => child !== null);
        return { ...node, children: updatedChildren };
      }
      return node.id === nodeId ? null : node;
    };

    const updatedRoot = removeFromGroup(conditionRoot) as ConditionGroup | null;
    // If root becomes empty, set to null
    if (updatedRoot && updatedRoot.children.length === 0) {
      onChange(null);
    } else {
      onChange(updatedRoot);
    }
  };

  // Handle updating a condition
  const handleUpdateCondition = (conditionId: string, updates: Partial<AnswerOptionCondition>) => {
    if (!conditionRoot) return;

    const updateInGroup = (node: ConditionNode): ConditionNode => {
      if (isConditionGroup(node)) {
        return { ...node, children: node.children.map(updateInGroup) };
      }
      return node.id === conditionId ? { ...node, ...updates } : node;
    };

    onChange(updateInGroup(conditionRoot) as ConditionGroup);
  };

  // Handle changing group logic
  const handleChangeGroupLogic = (groupId: string, logic: 'AND' | 'OR') => {
    if (!conditionRoot) return;

    const updateGroupLogic = (node: ConditionNode): ConditionNode => {
      if (isConditionGroup(node)) {
        if (node.id === groupId) {
          return { ...node, logic };
        }
        return { ...node, children: node.children.map(updateGroupLogic) };
      }
      return node;
    };

    onChange(updateGroupLogic(conditionRoot) as ConditionGroup);
  };

  // Handle adding keyword to AI condition
  const handleAddKeyword = (conditionId: string, keyword: string) => {
    if (!conditionRoot || !keyword.trim()) return;

    const findCondition = (node: ConditionNode): AnswerOptionCondition | null => {
      if (isConditionGroup(node)) {
        for (const child of node.children) {
          const found = findCondition(child);
          if (found) return found;
        }
        return null;
      }
      return node.id === conditionId ? node : null;
    };

    const condition = findCondition(conditionRoot);
    if (!condition) return;

    const currentKeywords = condition.aiHints?.nearKeywords || [];
    if (!currentKeywords.includes(keyword.trim())) {
      handleUpdateCondition(conditionId, {
        aiHints: {
          ...condition.aiHints,
          nearKeywords: [...currentKeywords, keyword.trim()],
        },
      });
    }
  };

  // Handle removing keyword from AI condition
  const handleRemoveKeyword = (conditionId: string, keyword: string) => {
    if (!conditionRoot) return;

    const findCondition = (node: ConditionNode): AnswerOptionCondition | null => {
      if (isConditionGroup(node)) {
        for (const child of node.children) {
          const found = findCondition(child);
          if (found) return found;
        }
        return null;
      }
      return node.id === conditionId ? node : null;
    };

    const condition = findCondition(conditionRoot);
    if (!condition) return;

    handleUpdateCondition(conditionId, {
      aiHints: {
        ...condition.aiHints,
        nearKeywords: (condition.aiHints?.nearKeywords || []).filter((k) => k !== keyword),
      },
    });
  };

  return (
    <div className="mt-2" data-tour="dd-conditions-builder">
      {/* Header */}
      <button
        type="button"
        className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? <CaretDown className="h-3 w-3" /> : <CaretRight className="h-3 w-3" />}
        <span>Voorwaarden ({totalConditions})</span>
        {conditionRoot && (
          <Badge variant="outline" className="text-[10px] px-1 py-0">
            {conditionRoot.logic}
          </Badge>
        )}
      </button>

      {isExpanded && (
        <div className="mt-2 space-y-3">
          {/* Explanation */}
          {totalConditions > 0 && (
            <p className="text-xs text-muted-foreground">
              Definieer voorwaarden met EN/OF logica. Groepen kunnen geneste subgroepen bevatten voor complexe logica.
              {hasMultipleLabels && ' Je kunt per voorwaarde specificeren op welk label deze van toepassing is.'}
            </p>
          )}

          {/* Render the root group or show empty state */}
          {conditionRoot ? (
            <ConditionGroupRenderer
              group={conditionRoot}
              depth={0}
              linkedLabels={linkedLabels}
              hasMultipleLabels={hasMultipleLabels}
              extractionQuestions={extractionQuestions}
              availableQuestions={availableQuestions}
              currentQuestionId={currentQuestionId}
              availableConditionTypes={availableConditionTypes}
              onAddCondition={handleAddCondition}
              onAddGroup={handleAddGroup}
              onRemoveNode={handleRemoveNode}
              onUpdateCondition={handleUpdateCondition}
              onChangeGroupLogic={handleChangeGroupLogic}
              onAddKeyword={handleAddKeyword}
              onRemoveKeyword={handleRemoveKeyword}
            />
          ) : (
            <div className="p-4 border border-dashed rounded-lg bg-muted/20">
              <p className="text-sm text-muted-foreground mb-3">Nog geen voorwaarden. Voeg een voorwaarde toe om te beginnen.</p>
              <div className="flex flex-wrap gap-1">
                {availableConditionTypes.map((condType) => (
                  <Button
                    key={condType.type}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs gap-1"
                    onClick={() => {
                      const newRoot = createNewGroup('AND');
                      newRoot.children.push(createNewCondition(condType.type));
                      onChange(newRoot);
                      setIsExpanded(true);
                    }}
                  >
                    <condType.icon className="h-3 w-3" />
                    {condType.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// === Condition Group Renderer (Recursive) ===

interface ConditionGroupRendererProps {
  group: ConditionGroup;
  depth: number;
  linkedLabels: LinkedLabel[];
  hasMultipleLabels: boolean;
  extractionQuestions: ExtractionQuestion[];
  availableQuestions: AvailableQuestion[];
  currentQuestionId?: number;
  availableConditionTypes: typeof CONDITION_TYPES;
  onAddCondition: (groupId: string, type: AnswerOptionConditionType) => void;
  onAddGroup: (parentGroupId: string) => void;
  onRemoveNode: (nodeId: string) => void;
  onUpdateCondition: (conditionId: string, updates: Partial<AnswerOptionCondition>) => void;
  onChangeGroupLogic: (groupId: string, logic: 'AND' | 'OR') => void;
  onAddKeyword: (conditionId: string, keyword: string) => void;
  onRemoveKeyword: (conditionId: string, keyword: string) => void;
}

const ConditionGroupRenderer: React.FC<ConditionGroupRendererProps> = ({
  group,
  depth,
  linkedLabels,
  hasMultipleLabels,
  extractionQuestions,
  availableQuestions,
  currentQuestionId,
  availableConditionTypes,
  onAddCondition,
  onAddGroup,
  onRemoveNode,
  onUpdateCondition,
  onChangeGroupLogic,
  onAddKeyword,
  onRemoveKeyword,
}) => {
  const isRootGroup = depth === 0;

  // Calculate border colors based on depth for visual hierarchy
  const getBorderColor = () => {
    const colors = ['border-blue-200', 'border-purple-200', 'border-green-200', 'border-orange-200'];
    return colors[depth % colors.length];
  };

  return (
    <div className={`rounded-lg border ${getBorderColor()} ${isRootGroup ? 'p-3' : 'p-2 ml-4 bg-muted/10'}`}>
      {/* Group Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {!isRootGroup && <TreeStructure className="h-4 w-4 text-muted-foreground" />}
          <span className="text-xs font-medium text-muted-foreground">
            {isRootGroup ? 'Voorwaarden' : 'Subgroep'}
          </span>

          {/* AND/OR Toggle */}
          <div className="flex gap-0.5 ml-2">
            <Button
              type="button"
              variant={group.logic === 'AND' ? 'default' : 'outline'}
              size="sm"
              className="h-6 text-[10px] px-2"
              onClick={() => onChangeGroupLogic(group.id, 'AND')}
            >
              EN
            </Button>
            <Button
              type="button"
              variant={group.logic === 'OR' ? 'default' : 'outline'}
              size="sm"
              className="h-6 text-[10px] px-2"
              onClick={() => onChangeGroupLogic(group.id, 'OR')}
            >
              OF
            </Button>
          </div>
        </div>

        {!isRootGroup && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-destructive"
            onClick={() => onRemoveNode(group.id)}
          >
            <Trash className="h-3 w-3" />
          </Button>
        )}
      </div>

      {/* Logic explanation */}
      <p className="text-[10px] text-muted-foreground mb-2">
        {group.logic === 'AND'
          ? 'Alle voorwaarden in deze groep moeten matchen'
          : 'Minstens één voorwaarde in deze groep moet matchen'}
      </p>

      {/* Children */}
      <div className="space-y-2">
        {group.children.map((child, index) => (
          <div key={child.id}>
            {/* Logic separator between items */}
            {index > 0 && (
              <div className="flex items-center justify-center py-1">
                <span className="text-[10px] text-muted-foreground px-2 bg-muted rounded">
                  {group.logic}
                </span>
              </div>
            )}

            {isConditionGroup(child) ? (
              // Render nested group recursively
              <ConditionGroupRenderer
                group={child}
                depth={depth + 1}
                linkedLabels={linkedLabels}
                hasMultipleLabels={hasMultipleLabels}
                extractionQuestions={extractionQuestions}
                availableQuestions={availableQuestions}
                currentQuestionId={currentQuestionId}
                availableConditionTypes={availableConditionTypes}
                onAddCondition={onAddCondition}
                onAddGroup={onAddGroup}
                onRemoveNode={onRemoveNode}
                onUpdateCondition={onUpdateCondition}
                onChangeGroupLogic={onChangeGroupLogic}
                onAddKeyword={onAddKeyword}
                onRemoveKeyword={onRemoveKeyword}
              />
            ) : (
              // Render single condition
              <ConditionRenderer
                condition={child}
                linkedLabels={linkedLabels}
                hasMultipleLabels={hasMultipleLabels}
                extractionQuestions={extractionQuestions}
                availableQuestions={availableQuestions}
                currentQuestionId={currentQuestionId}
                onUpdate={(updates) => onUpdateCondition(child.id, updates)}
                onRemove={() => onRemoveNode(child.id)}
                onAddKeyword={(kw) => onAddKeyword(child.id, kw)}
                onRemoveKeyword={(kw) => onRemoveKeyword(child.id, kw)}
              />
            )}
          </div>
        ))}
      </div>

      {/* Add buttons */}
      <div className="flex flex-wrap gap-1 mt-3 pt-2 border-t border-dashed">
        {availableConditionTypes.map((condType) => (
          <Button
            key={condType.type}
            type="button"
            variant="outline"
            size="sm"
            className="h-6 text-[10px] gap-1"
            onClick={() => onAddCondition(group.id, condType.type)}
          >
            <condType.icon className="h-3 w-3" />
            {condType.label}
          </Button>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-6 text-[10px] gap-1 border-dashed"
          onClick={() => onAddGroup(group.id)}
        >
          <TreeStructure className="h-3 w-3" />
          + Subgroep
        </Button>
      </div>
    </div>
  );
};

// === Single Condition Renderer ===

interface ConditionRendererProps {
  condition: AnswerOptionCondition;
  linkedLabels: LinkedLabel[];
  hasMultipleLabels: boolean;
  extractionQuestions: ExtractionQuestion[];
  availableQuestions: AvailableQuestion[];
  currentQuestionId?: number;
  onUpdate: (updates: Partial<AnswerOptionCondition>) => void;
  onRemove: () => void;
  onAddKeyword: (keyword: string) => void;
  onRemoveKeyword: (keyword: string) => void;
}

const ConditionRenderer: React.FC<ConditionRendererProps> = ({
  condition,
  linkedLabels,
  hasMultipleLabels,
  extractionQuestions,
  availableQuestions,
  currentQuestionId,
  onUpdate,
  onRemove,
  onAddKeyword,
  onRemoveKeyword,
}) => {
  const meta = CONDITION_TYPES.find((t) => t.type === condition.type);
  const Icon = meta?.icon || File;

  return (
    <div className="p-3 bg-background rounded-lg border border-muted">
      {/* Condition header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">{meta?.label}</span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
        >
          <Trash className="h-3 w-3" />
        </Button>
      </div>

      {/* Label selector - only show when multiple labels are linked */}
      {hasMultipleLabels && (
        <div className="mb-3 p-2 bg-muted/30 rounded border">
          <Label className="text-xs text-muted-foreground mb-1.5 block">
            Van toepassing op label:
          </Label>
          <SimpleDropdown
            value={condition.labelId?.toString() || 'all'}
            onChange={(v) => onUpdate({ labelId: v === 'all' ? undefined : parseInt(v) })}
            options={[
              { value: 'all', label: 'Alle gekoppelde labels' },
              ...linkedLabels.map((l) => ({ value: l.id.toString(), label: l.name })),
            ]}
            triggerClassName="h-8 text-xs"
          />
        </div>
      )}

      {/* Condition-specific editor */}
      {condition.type === AnswerOptionConditionType.FILE_EXISTS && (
        <FileExistsEditor condition={condition} onUpdate={onUpdate} linkedLabels={linkedLabels} />
      )}

      {condition.type === AnswerOptionConditionType.EXPIRATION_DATE && (
        <ExpirationDateEditor condition={condition} onUpdate={onUpdate} linkedLabels={linkedLabels} />
      )}

      {condition.type === AnswerOptionConditionType.FIELD_VALUE && (
        <FieldValueEditor condition={condition} onUpdate={onUpdate} />
      )}

      {condition.type === AnswerOptionConditionType.AI_QUESTION && (
        <AIQuestionEditor
          condition={condition}
          onUpdate={onUpdate}
          onAddKeyword={onAddKeyword}
          onRemoveKeyword={onRemoveKeyword}
        />
      )}

      {condition.type === AnswerOptionConditionType.AI_REFERENCE && (
        <AIReferenceEditor
          condition={condition}
          onUpdate={onUpdate}
          onAddKeyword={onAddKeyword}
          onRemoveKeyword={onRemoveKeyword}
        />
      )}

      {condition.type === AnswerOptionConditionType.DATABASE_COMPARISON && (
        <DatabaseComparisonConditionEditor
          condition={condition}
          onUpdate={onUpdate}
          extractionQuestions={extractionQuestions}
        />
      )}

      {condition.type === AnswerOptionConditionType.QUESTION_ANSWER && (
        <QuestionAnswerConditionEditor
          condition={condition}
          onUpdate={onUpdate}
          availableQuestions={availableQuestions}
          currentQuestionId={currentQuestionId}
        />
      )}
    </div>
  );
};

// === Sub-editors for each condition type ===

interface ConditionEditorProps {
  condition: AnswerOptionCondition;
  onUpdate: (updates: Partial<AnswerOptionCondition>) => void;
}

interface AIConditionEditorProps extends ConditionEditorProps {
  onAddKeyword: (keyword: string) => void;
  onRemoveKeyword: (keyword: string) => void;
}

// FileExistsEditor is imported from ./ConditionBuilder/editors/FileExistsEditor

// ExpirationDateEditor is imported from ./ConditionBuilder/editors/ExpirationDateEditor

const FieldValueEditor: React.FC<ConditionEditorProps> = ({ condition, onUpdate }) => {
  const operatorOptions = [
    { value: '==', label: 'Gelijk aan (==)' },
    { value: '!=', label: 'Niet gelijk aan (!=)' },
    { value: 'contains', label: 'Bevat' },
    { value: 'not_empty', label: 'Niet leeg' },
    { value: 'is_empty', label: 'Leeg' },
  ];

  const selectedField = condition.field || 'validity';
  const fieldEnumOptions = FIELD_ENUM_VALUES[selectedField];
  const needsValueInput = !['not_empty', 'is_empty'].includes(condition.operator || '==');

  // When changing field, reset value if the new field has enum options
  const handleFieldChange = (newField: string) => {
    const newEnumOptions = FIELD_ENUM_VALUES[newField];
    if (newEnumOptions && newEnumOptions.length > 0) {
      // Reset to first enum value
      onUpdate({ field: newField, value: newEnumOptions[0].value });
    } else {
      onUpdate({ field: newField, value: '' });
    }
  };

  return (
    <div className={`grid gap-3 ${needsValueInput ? 'grid-cols-3' : 'grid-cols-2'}`}>
      <div className="space-y-1.5">
        <Label className="text-xs">Veld</Label>
        <SimpleDropdown
          value={selectedField}
          onChange={handleFieldChange}
          options={FILELOG_FIELDS}
          triggerClassName="h-9 text-sm"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Operator</Label>
        <SimpleDropdown
          value={condition.operator || '=='}
          onChange={(v) => onUpdate({ operator: v as any })}
          options={operatorOptions}
          triggerClassName="h-9 text-sm"
        />
      </div>
      {needsValueInput && (
        <div className="space-y-1.5">
          <Label className="text-xs">Waarde</Label>
          {fieldEnumOptions ? (
            <SimpleDropdown
              value={String(condition.value || fieldEnumOptions[0]?.value || '')}
              onChange={(v) => onUpdate({ value: v })}
              options={fieldEnumOptions}
              triggerClassName="h-9 text-sm"
            />
          ) : (
            <Input
              value={String(condition.value || '')}
              onChange={(e) => onUpdate({ value: e.target.value })}
              className="h-9 text-sm"
              placeholder="Voer waarde in..."
            />
          )}
        </div>
      )}
    </div>
  );
};

const AIQuestionEditor: React.FC<AIConditionEditorProps> = ({
  condition,
  onUpdate,
  onAddKeyword,
  onRemoveKeyword,
}) => {
  const [newKeyword, setNewKeyword] = useState('');

  const expectedAnswerOptions = [
    { value: 'yes', label: 'Ja' },
    { value: 'no', label: 'Nee' },
  ];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2 space-y-1.5">
          <Label className="text-xs">Vraag aan AI</Label>
          <Input
            value={condition.aiQuestion || ''}
            onChange={(e) => onUpdate({ aiQuestion: e.target.value })}
            className="h-9 text-sm"
            placeholder="bijv. Is het document ondertekend?"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Indien antwoord</Label>
          <SimpleDropdown
            value={condition.expectedAnswer || 'yes'}
            onChange={(v) => onUpdate({ expectedAnswer: v as 'yes' | 'no' })}
            options={expectedAnswerOptions}
            triggerClassName="h-9 text-sm"
          />
        </div>
      </div>

      <AILocationHintsEditor
        hints={condition.aiHints}
        onUpdate={(hints) => onUpdate({ aiHints: hints })}
        onAddKeyword={onAddKeyword}
        onRemoveKeyword={onRemoveKeyword}
        newKeyword={newKeyword}
        setNewKeyword={setNewKeyword}
      />
    </div>
  );
};

const AIReferenceEditor: React.FC<AIConditionEditorProps> = ({
  condition,
  onUpdate,
  onAddKeyword,
  onRemoveKeyword,
}) => {
  const [newKeyword, setNewKeyword] = useState('');

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label className="text-xs">Zoek referentie</Label>
        <Input
          value={condition.aiReference || ''}
          onChange={(e) => onUpdate({ aiReference: e.target.value })}
          className="h-9 text-sm"
          placeholder="bijv. Vervaldatum / Expiration Date"
        />
        <p className="text-[10px] text-muted-foreground">
          AI zoekt deze tekst/referentie in het document en extraheert de waarde
        </p>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Filter op waarde (optioneel)</Label>
        <Input
          value={condition.aiExpectedValue || ''}
          onChange={(e) => onUpdate({ aiExpectedValue: e.target.value })}
          className="h-9 text-sm"
          placeholder="bijv. Geldig, Approved, etc."
        />
      </div>

      <AILocationHintsEditor
        hints={condition.aiHints}
        onUpdate={(hints) => onUpdate({ aiHints: hints })}
        onAddKeyword={onAddKeyword}
        onRemoveKeyword={onRemoveKeyword}
        newKeyword={newKeyword}
        setNewKeyword={setNewKeyword}
      />
    </div>
  );
};

interface AILocationHintsEditorProps {
  hints?: AILocationHints;
  onUpdate: (hints: AILocationHints) => void;
  onAddKeyword: (keyword: string) => void;
  onRemoveKeyword: (keyword: string) => void;
  newKeyword: string;
  setNewKeyword: (value: string) => void;
}

const AILocationHintsEditor: React.FC<AILocationHintsEditorProps> = ({
  hints,
  onUpdate,
  onAddKeyword,
  onRemoveKeyword,
  newKeyword,
  setNewKeyword,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newKeyword.trim()) {
      e.preventDefault();
      onAddKeyword(newKeyword);
      setNewKeyword('');
    }
  };

  return (
    <div className="p-3 bg-muted/30 rounded border border-dashed space-y-3">
      <Label className="text-xs text-muted-foreground">
        Zoek hints (optioneel) - Waar kan AI het antwoord vinden?
      </Label>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Nabij pagina's</Label>
          <Input
            value={hints?.nearPages || ''}
            onChange={(e) => onUpdate({ ...hints, nearPages: e.target.value })}
            className="h-9 text-sm"
            placeholder="bijv. laatste pagina's, pagina 10"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Nabij titel/sectie</Label>
          <Input
            value={hints?.nearTitle || ''}
            onChange={(e) => onUpdate({ ...hints, nearTitle: e.target.value })}
            className="h-9 text-sm"
            placeholder="bijv. Conclusie, Handtekening"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Nabij keywords</Label>
        <div className="flex flex-wrap items-center gap-1.5">
          {(hints?.nearKeywords || []).map((kw) => (
            <Badge key={kw} variant="secondary" className="text-xs px-2 py-0.5 gap-1">
              {kw}
              <button type="button" onClick={() => onRemoveKeyword(kw)} className="hover:text-destructive">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <Input
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-8 w-36 text-sm"
            placeholder="+ keyword"
          />
        </div>
      </div>
    </div>
  );
};

// === Database Comparison Condition Editor ===

// Database fields available for comparison (matching backend AVAILABLE_DATABASE_FIELDS)
const DATABASE_FIELDS = [
  {
    category: 'building',
    fields: [
      // Computed/derived fields
      { key: 'building.unitsCount', label: 'Aantal units', description: 'Berekend: het aantal geregistreerde units (building.units.length)' },
      // Direct building fields
      { key: 'building.constructionYear', label: 'Bouwjaar', description: 'Het jaar waarin het gebouw is gebouwd' },
      { key: 'building.buildingType', label: 'Gebouwtype', description: 'Het type gebouw (Opbrengsteigendom, Woning, Retail)' },
      { key: 'building.googlePlaceLongAddress', label: 'Adres', description: 'Het volledige adres van het gebouw' },
      { key: 'building.amountOfFloors', label: 'Aantal verdiepingen', description: 'Het aantal verdiepingen in het gebouw' },
      { key: 'building.nettoSurfaceInclCommonAreas', label: 'Netto oppervlakte (incl. gemeen.)', description: 'Netto oppervlakte inclusief gemeenschappelijke ruimtes (m²)' },
      { key: 'building.nettoSurfaceExclCommonAreas', label: 'Netto oppervlakte (excl. gemeen.)', description: 'Netto oppervlakte exclusief gemeenschappelijke ruimtes (m²)' },
      { key: 'building.plotSurface', label: 'Perceeloppervlakte', description: 'De oppervlakte van het perceel (m²)' },
      { key: 'building.numUnitsWithPermit', label: 'Aantal units met vergunning', description: 'Aantal wooneenheden met vergunning' },
      { key: 'building.numUnitsWithoutPermit', label: 'Aantal units zonder vergunning', description: 'Aantal wooneenheden zonder vergunning' },
      { key: 'building.permitStatus', label: 'Vergunningsstatus', description: 'Status van vergunningen (niet vergund, stedenbouwkundige vergunning verkregen, etc.)' },
      { key: 'building.heritageStatus', label: 'Erfgoedstatus', description: 'Erfgoedstatus van het gebouw' },
      { key: 'building.mobiscore', label: 'Mobiscore', description: 'De mobiscore van het gebouw' },
      { key: 'building.acquisitionPrice', label: 'Aankoopprijs', description: 'De aankoopprijs van het gebouw' },
      { key: 'building.acquisitionDate', label: 'Aankoopdatum', description: 'De datum van aankoop' },
      { key: 'building.estimatedMarketValue', label: 'Geschatte marktwaarde', description: 'De geschatte huidige marktwaarde' },
      { key: 'building.strategicStatus', label: 'Strategische status', description: 'Strategische status (Afstoten, Flexibel behouden, etc.)' },
      { key: 'building.ownershipType', label: 'Eigendomstype', description: 'Type eigendom (Huur, Eigendom, In erfpacht gegeven, etc.)' },
      // BREEAM certification
      { key: 'building.breeamRating', label: 'BREEAM rating', description: 'BREEAM duurzaamheidscertificering rating' },
      { key: 'building.breeamScorePercent', label: 'BREEAM score (%)', description: 'BREEAM score in percentage' },
      // WELL certification
      { key: 'building.wellRating', label: 'WELL rating', description: 'WELL gezondheid- en welzijnscertificering rating' },
      { key: 'building.wellScorePoints', label: 'WELL score (punten)', description: 'WELL score in punten' },
    ],
  },
  {
    category: 'unit',
    fields: [
      { key: 'unit.floor', label: 'Verdieping', description: 'De verdieping waarop de unit zich bevindt' },
      { key: 'unit.grossSurface', label: 'Bruto oppervlakte (m²)', description: 'De bruto oppervlakte van de unit' },
      { key: 'unit.netSurface', label: 'Netto oppervlakte (m²)', description: 'De netto oppervlakte van de unit' },
      { key: 'unit.unitType', label: 'Type unit', description: 'Het type unit (Residentieel, Commercieel, Overig)' },
      { key: 'unit.unitSubtype', label: 'Subtype unit', description: 'Het subtype (Appartement, Studio, Kantoor, Garage, etc.)' },
      { key: 'unit.estimatedRentalValue', label: 'Geschatte huurwaarde', description: 'De geschatte huurwaarde van de unit' },
      { key: 'unit.estimatedCommonCosts', label: 'Geschatte gemeensch. kosten', description: 'De geschatte gemeenschappelijke kosten' },
      { key: 'unit.advertisedRentalPrice', label: 'Geadverteerde huurprijs', description: 'De geadverteerde huurprijs' },
      { key: 'unit.rentStatus', label: 'Verhuurstatus', description: 'De verhuurstatus (Te huur, Verhuurd, Niet te huur)' },
      { key: 'unit.saleStatus', label: 'Verkoopstatus', description: 'De verkoopstatus (Te koop, Verkocht, Niet te koop)' },
      { key: 'unit.outsideSpace', label: 'Buitenruimte', description: 'Type buitenruimte (Geen, Terras, Tuin)' },
      { key: 'unit.outsideSpaceSurface', label: 'Buitenruimte oppervlakte', description: 'Oppervlakte van de buitenruimte (m²)' },
      { key: 'unit.furnished', label: 'Gemeubileerd', description: 'Of de unit gemeubileerd is' },
    ],
  },
];

const DATABASE_COMPARISON_OPERATORS = [
  { value: '==', label: 'Gelijk aan (==)' },
  { value: '!=', label: 'Niet gelijk aan (!=)' },
  { value: '>', label: 'Groter dan (>)' },
  { value: '<', label: 'Kleiner dan (<)' },
  { value: '>=', label: 'Groter of gelijk (>=)' },
  { value: '<=', label: 'Kleiner of gelijk (<=)' },
];

const DATABASE_COMPARISON_EXPECTED = [
  { value: 'match', label: 'Waarden komen overeen' },
  { value: 'mismatch', label: 'Waarden wijken af' },
  { value: 'document_value_exists', label: 'Document waarde bestaat' },
];

interface DatabaseComparisonEditorProps extends ConditionEditorProps {
  extractionQuestions: ExtractionQuestion[];
}

const DatabaseComparisonConditionEditor: React.FC<DatabaseComparisonEditorProps> = ({
  condition,
  onUpdate,
  extractionQuestions,
}) => {
  const allFields = DATABASE_FIELDS.flatMap((cat) => cat.fields);
  const selectedField = allFields.find((f) => f.key === condition.databaseField);
  const needsAiPrompt = condition.documentValueSource === 'aiExtraction';
  const needsExtractionQuestion = condition.documentValueSource === 'extractionQuestion';

  // Build document value source options dynamically
  const documentValueSourceOptions = [
    ...(extractionQuestions.length > 0
      ? [{ value: 'extractionQuestion', label: 'Extractie vraag (uit categorie)' }]
      : []),
    { value: 'aiExtraction', label: 'AI extractie (directe vraag)' },
  ];

  // Find selected extraction question
  const selectedExtractionQuestion = extractionQuestions.find(
    (q) => q.fieldName === condition.extractionQuestionFieldName
  );

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 p-2 bg-purple-50 rounded text-xs text-purple-800">
        <Database size={14} className="mt-0.5 flex-shrink-0" />
        <span>
          Vergelijk een waarde uit het document met een veld uit de database (gebouw/unit).
        </span>
      </div>

      {/* Document value source */}
      <div className="space-y-1.5">
        <Label className="text-xs">Document waarde ophalen via</Label>
        <SimpleDropdown
          value={condition.documentValueSource || (extractionQuestions.length > 0 ? 'extractionQuestion' : 'aiExtraction')}
          onChange={(v) => onUpdate({ documentValueSource: v as 'extractionQuestion' | 'aiExtraction' })}
          options={documentValueSourceOptions}
          triggerClassName="h-9 text-sm"
        />
        <p className="text-[10px] text-muted-foreground">
          {condition.documentValueSource === 'aiExtraction'
            ? 'AI extraheert de waarde direct uit het document met een vraag'
            : condition.documentValueSource === 'extractionQuestion'
            ? 'Gebruikt de geëxtraheerde waarde van een extractie vraag uit deze categorie'
            : 'Selecteer hoe de waarde moet worden opgehaald'}
        </p>
      </div>

      {/* Extraction question selector (when extractionQuestion is selected) */}
      {needsExtractionQuestion && (
        <div className="space-y-1.5">
          <Label className="text-xs">Selecteer extractie vraag</Label>
          {extractionQuestions.length > 0 ? (
            <>
              <SimpleDropdown
                value={condition.extractionQuestionFieldName || ''}
                onChange={(v) => onUpdate({ extractionQuestionFieldName: v })}
                options={[
                  { value: '', label: 'Selecteer vraag...' },
                  ...extractionQuestions.map((q) => ({
                    value: q.fieldName,
                    label: q.extractionLabel || q.displayName,
                  })),
                ]}
                triggerClassName="h-9 text-sm"
              />
              {selectedExtractionQuestion && (
                <p className="text-[10px] text-muted-foreground">
                  Waarde type: <span className="font-medium">{
                    selectedExtractionQuestion.extractionValueType === 'number' ? 'Getal' :
                    selectedExtractionQuestion.extractionValueType === 'string' ? 'Tekst' :
                    selectedExtractionQuestion.extractionValueType === 'date' ? 'Datum' :
                    selectedExtractionQuestion.extractionValueType === 'boolean' ? 'Ja/Nee' : 'Onbekend'
                  }</span>
                </p>
              )}
            </>
          ) : (
            <p className="text-xs text-amber-600">
              Geen extractie vragen beschikbaar. Voeg eerst een vraag toe met type "Extractie (geen status)".
            </p>
          )}
        </div>
      )}

      {/* AI extraction prompt (only when aiExtraction is selected) */}
      {needsAiPrompt && (
        <div className="space-y-1.5">
          <Label className="text-xs">AI extractie vraag</Label>
          <Input
            value={condition.documentExtractionPrompt || ''}
            onChange={(e) => onUpdate({ documentExtractionPrompt: e.target.value })}
            className="h-9 text-sm"
            placeholder="bijv. Hoeveel wooneenheden worden in dit document vermeld?"
          />
        </div>
      )}

      {/* Database field selection */}
      <div className="space-y-1.5">
        <Label className="text-xs">Database veld</Label>
        <SimpleDropdown
          value={condition.databaseField || ''}
          onChange={(v) => onUpdate({ databaseField: v })}
          options={[
            { value: '', label: 'Selecteer veld...' },
            ...DATABASE_FIELDS.flatMap((cat) => [
              { value: `__header_${cat.category}`, label: cat.category === 'building' ? '── Gebouw ──' : '── Unit ──', disabled: true },
              ...cat.fields.map((f) => ({ value: f.key, label: f.label })),
            ]),
          ].filter((opt) => !opt.value?.startsWith('__header_') || true)}
          triggerClassName="h-9 text-sm"
        />
        {selectedField && (
          <p className="text-[10px] text-muted-foreground">
            {selectedField.description}
          </p>
        )}
      </div>

      {/* Comparison settings */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Vergelijking</Label>
          <SimpleDropdown
            value={condition.dbComparisonOperator || '=='}
            onChange={(v) => onUpdate({ dbComparisonOperator: v as any })}
            options={DATABASE_COMPARISON_OPERATORS}
            triggerClassName="h-9 text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Voorwaarde voldaan als</Label>
          <SimpleDropdown
            value={condition.dbComparisonExpected || 'match'}
            onChange={(v) => onUpdate({ dbComparisonExpected: v as any })}
            options={DATABASE_COMPARISON_EXPECTED}
            triggerClassName="h-9 text-sm"
          />
        </div>
      </div>

      {/* Explanation of what will happen */}
      <div className="p-2 bg-muted/30 rounded border border-dashed text-xs text-muted-foreground">
        <p className="font-medium mb-1">Voorbeeld:</p>
        {condition.databaseField ? (
          <p>
            Als de document waarde{' '}
            <span className="font-mono bg-muted px-1 rounded">
              {condition.dbComparisonOperator || '=='} {selectedField?.label || condition.databaseField}
            </span>{' '}
            en {condition.dbComparisonExpected === 'match' ? 'de waarden komen overeen' :
                condition.dbComparisonExpected === 'mismatch' ? 'de waarden wijken af' :
                'de document waarde bestaat'},
            dan is deze voorwaarde voldaan.
          </p>
        ) : (
          <p>Selecteer een database veld om een voorbeeld te zien.</p>
        )}
      </div>
    </div>
  );
};

// === Question Answer Condition Editor ===

const QUESTION_ANSWER_COMPARISON_OPTIONS = [
  { value: 'equals', label: 'Is gelijk aan' },
  { value: 'not_equals', label: 'Is niet gelijk aan' },
  { value: 'any_of', label: 'Is een van' },
];

interface QuestionAnswerConditionEditorProps extends ConditionEditorProps {
  availableQuestions: AvailableQuestion[];
  currentQuestionId?: number;
}

const QuestionAnswerConditionEditor: React.FC<QuestionAnswerConditionEditorProps> = ({
  condition,
  onUpdate,
  availableQuestions,
  currentQuestionId,
}) => {
  // Filter out the current question
  const otherQuestions = availableQuestions.filter((q) => q.id !== currentQuestionId);

  // Get selected question's answer options (include unsaved options too)
  const selectedQuestion = otherQuestions.find((q) => q.id === condition.referencedQuestionId);
  const allAnswerOptions = selectedQuestion?.answerOptions?.filter((o) => o.active) ?? [];

  // Handle question change - reset expected option
  const handleQuestionChange = (questionId: string) => {
    const id = questionId ? parseInt(questionId) : undefined;
    onUpdate({
      referencedQuestionId: id,
      expectedOptionId: undefined,
      expectedOptionValue: undefined,
      expectedOptionIds: undefined,
    });
  };

  // Handle option selection - store both ID (if available) and value
  const handleOptionChange = (optionValue: string) => {
    if (!optionValue) {
      onUpdate({ expectedOptionId: undefined, expectedOptionValue: undefined });
      return;
    }
    const selectedOption = allAnswerOptions.find((o) => o.value === optionValue);
    onUpdate({
      expectedOptionId: selectedOption?.id ?? undefined,
      expectedOptionValue: optionValue,
    });
  };

  // Get current selected value (prefer value over id for display)
  const currentSelectedValue = condition.expectedOptionValue ||
    allAnswerOptions.find((o) => o.id === condition.expectedOptionId)?.value ||
    '';

  const getStatusColorClass = (color: string) => {
    switch (color) {
      case 'green':
        return 'bg-green-500';
      case 'orange':
        return 'bg-orange-500';
      case 'red':
        return 'bg-red-500';
      default:
        return 'bg-gray-300';
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 p-2 bg-blue-50 rounded text-xs text-blue-800">
        <GitBranch size={14} className="mt-0.5 flex-shrink-0" />
        <span>
          Controleert het antwoord van een andere vraag in deze categorie.
          Hiermee kun je afhankelijkheden tussen vragen creëren.
        </span>
      </div>

      {otherQuestions.length === 0 ? (
        <p className="text-xs text-amber-600">
          Geen andere vragen beschikbaar in deze categorie om naar te verwijzen.
        </p>
      ) : (
        <>
          {/* Question selector */}
          <div className="space-y-1.5">
            <Label className="text-xs">Afhankelijk van vraag</Label>
            <SimpleDropdown
              value={condition.referencedQuestionId?.toString() || ''}
              onChange={handleQuestionChange}
              options={[
                { value: '', label: 'Selecteer vraag...' },
                ...otherQuestions.map((q) => ({
                  value: q.id.toString(),
                  label: q.displayName,
                })),
              ]}
              triggerClassName="h-9 text-sm"
            />
          </div>

          {/* Comparison type */}
          <div className="space-y-1.5">
            <Label className="text-xs">Vergelijking</Label>
            <SimpleDropdown
              value={condition.questionAnswerComparison || 'equals'}
              onChange={(v) => onUpdate({ questionAnswerComparison: v as 'equals' | 'not_equals' | 'any_of' })}
              options={QUESTION_ANSWER_COMPARISON_OPTIONS}
              triggerClassName="h-9 text-sm"
            />
          </div>

          {/* Answer option selector - only show when question is selected */}
          {selectedQuestion && allAnswerOptions.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs">Indien antwoord</Label>
              <SimpleDropdown
                value={currentSelectedValue}
                onChange={handleOptionChange}
                options={[
                  { value: '', label: 'Selecteer antwoord...' },
                  ...allAnswerOptions.map((opt) => ({
                    value: opt.value,
                    label: opt.label + (opt.id == null ? ' (nog niet opgeslagen)' : ''),
                    icon: (
                      <span
                        className={`inline-block w-2.5 h-2.5 rounded-full mr-2 ${getStatusColorClass(opt.statusColor)}`}
                      />
                    ),
                  })),
                ]}
                triggerClassName="h-9 text-sm"
              />
            </div>
          )}

          {selectedQuestion && allAnswerOptions.length === 0 && (
            <p className="text-xs text-amber-600">
              De geselecteerde vraag heeft geen actieve antwoordopties.
            </p>
          )}

          {/* Explanation */}
          {condition.referencedQuestionId && (condition.expectedOptionValue || condition.expectedOptionId) && (
            <div className="p-2 bg-muted/30 rounded border border-dashed text-xs text-muted-foreground">
              <p className="font-medium mb-1">Resultaat:</p>
              <p>
                Deze voorwaarde is voldaan als de vraag "
                <span className="font-medium">{selectedQuestion?.displayName}</span>"{' '}
                {condition.questionAnswerComparison === 'equals'
                  ? 'gelijk is aan'
                  : condition.questionAnswerComparison === 'not_equals'
                  ? 'niet gelijk is aan'
                  : 'een van de volgende is:'}{' '}
                <span className="font-medium">
                  "{allAnswerOptions.find((o) => o.value === condition.expectedOptionValue || o.id === condition.expectedOptionId)?.label}"
                </span>
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AnswerOptionConditionBuilder;
